import React, { useEffect, useState } from "react";
import AppText from "./AppText";
import { isValidVerificationCode, isValidRecoveryCode } from "../utils";
import Input from "../../presentational/Input";
import LoadIndicator from "../../presentational/LoadIndicator";
import Checkbox from "../../presentational/Checkbox";
import Button from "../../presentational/Button";
import Links from "./Links";
import { appStrings } from "./strings";
import { interpolateLocaleString } from "../../../helpers/interpolateLocaleString";

import "./VerifyMfa.css";
import { renderTurnstile } from "../../../api/utils";

interface VerifyMfaProps {
    serverError: string;
    isLoading: boolean;
    allowRememberMe: boolean;
    rememberMe: boolean;
    rememberMeDays?: number;
    isTotpEnabled: boolean;
    hasRecoveryCodes: boolean;
    passkeyEnabled: boolean | undefined;
    verifyMfa: (code: string, clientVerificationToken: string, rememberMe?: boolean, isRecoveryCode?: boolean) => void;
    verifyPasskey?: () => Promise<boolean>;
    setRememberMe: (rememberMe: boolean) => void;
    getClientVerificationToken: (form: HTMLFormElement) => Promise<string>;
}

const VerifyMfa: React.FC<VerifyMfaProps> = ({
    serverError,
    isLoading,
    allowRememberMe,
    rememberMe,
    rememberMeDays,
    isTotpEnabled,
    hasRecoveryCodes,
    passkeyEnabled,
    verifyMfa,
    verifyPasskey,
    setRememberMe,
    getClientVerificationToken,
}) => {
    const [verificationCode, setVerificationCode] = useState("");
    const [autoPasskeyTriggered, setAutoPasskeyTriggered] = useState(false);
    const [autoPasskeyComplete, setAutoPasskeyComplete] = useState(false);
    const [useRecoveryCode, setUseRecoveryCode] = useState(!isTotpEnabled);

    const onSetVerificationCode = async (event: React.ChangeEvent<HTMLInputElement>) => {
        const code = event?.target?.value?.trim();
        setVerificationCode(code);
        if ((!useRecoveryCode && isValidVerificationCode(code)) || (useRecoveryCode && isValidRecoveryCode(code))) {
            const form = event.currentTarget.form!;
            verifyMfa(code, await getClientVerificationToken(form), rememberMe, useRecoveryCode);
        }
    };

    const handleChangeRememberMe = (e: React.ChangeEvent<HTMLInputElement>) => {
        setRememberMe(e.target.checked);
    };

    const toggleUseRecoveryCode = (e: React.MouseEvent<HTMLElement, MouseEvent>) => {
        e.stopPropagation();
        setVerificationCode("")
        setUseRecoveryCode(!useRecoveryCode);
    };

    const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        const form = e.currentTarget;
        verifyMfa(verificationCode, await getClientVerificationToken(form), rememberMe, useRecoveryCode);
    };

    useEffect(() => {
        if (verifyPasskey && !autoPasskeyTriggered) {
            setAutoPasskeyTriggered(true);
            const f = async () => {
                const response = await verifyPasskey!();
                if (!response) {
                    setAutoPasskeyComplete(true);
                }
            }
            f();
        }
    }, [verifyPasskey]);

    useEffect(() => {
        if ((passkeyEnabled === false || autoPasskeyComplete) && (isTotpEnabled || hasRecoveryCodes) && window.renderTurnstile) {
            return renderTurnstile();
        }
    }, [passkeyEnabled, autoPasskeyComplete, isTotpEnabled, hasRecoveryCodes]);

    const showLoadingInit = passkeyEnabled === undefined || (autoPasskeyTriggered && !autoPasskeyComplete);

    return (
        <main className="verify-mfa-page">
            <div className="form-wrapper">
                <form className="form" onSubmit={handleSubmit}>
                    {serverError && <p className="server-error">{serverError}</p>}
                    <div className="form-header">
                        <h2>
                            <AppText textKey="verify-mfa-title" />
                        </h2>
                        <p>
                            <AppText textKey="verify-mfa-info" />
                        </p>
                    </div>
                    {showLoadingInit &&
                        <LoadIndicator className="loading-icon" />
                    }
                    {!showLoadingInit && <>
                        {verifyPasskey && <>
                           <div>
                                <h4>
                                    <AppText textKey="passkey-title" />
                                </h4>
                                <p>
                                    <AppText textKey="verify-mfa-passkey-info" />
                                </p>
                           </div>
                            <Button intent="primary" type="button" loading={isLoading} onClick={verifyPasskey}>
                                <AppText textKey="verify-mfa-passkey-use-label" />
                            </Button>
                        </>}
                        {isTotpEnabled && !useRecoveryCode && <>
                            <div className="mfa-section">
                                <div>
                                    <h4>
                                        <AppText textKey="totp-title" />
                                    </h4>
                                    <p>
                                        <AppText textKey="verify-mfa-totp-info" />
                                    </p>
                                </div>
                                <div className="field-group">
                                    <label className="login-field" htmlFor="totpVerificationCode">
                                        <AppText textKey="totp-code-label" />
                                    </label>
                                    <Input
                                        autoComplete="one-time-code"
                                        className="autofocus"
                                        disabled={isLoading}
                                        onChange={onSetVerificationCode}
                                        id="totpVerificationCode"
                                        value={verificationCode}
                                        placeholder="123456"
                                        fullWidth={true}
                                        autoFocus={true}
                                        htmlAttributes={{
                                            maxLength: 6,
                                            pattern: "[0-9]{6}",
                                            spellCheck: false,
                                            autoCorrect: "off",
                                            autoCapitalize: "none",
                                        }}
                                    />
                                </div>
                                {allowRememberMe && (
                                    <Checkbox
                                        className="login-field"
                                        identifier="rememberMe"
                                        disabled={isLoading}
                                        checked={rememberMe}
                                        onChange={handleChangeRememberMe}
                                        label={
                                            rememberMeDays
                                                ? interpolateLocaleString(appStrings["verify-mfa-remember-me-in-days-label"], rememberMeDays ? { days: rememberMeDays } : undefined)
                                                : appStrings["verify-mfa-remember-me-label"]
                                        }
                                    />
                                )}
                                <Button
                                    intent="primary"
                                    disabled={isLoading || !isValidVerificationCode(verificationCode)}
                                    type="submit"
                                >
                                    <AppText textKey="verify-mfa-submit-label" />
                                </Button>
                            </div>
                            <div className="mfa-section">
                                <Button disabled={isLoading} onClick={toggleUseRecoveryCode} type="button">
                                    <AppText textKey="verify-mfa-use-recovery-code-label" />
                                </Button>
                            </div>

                        </>}
                        {hasRecoveryCodes && useRecoveryCode && <>
                            <div className="mfa-section">
                                <div>
                                    <p>
                                        <strong>
                                            <AppText textKey="verify-mfa-recovery-instructions-header" />
                                        </strong>
                                    </p>
                                    <p>
                                        <AppText textKey="verify-mfa-recovery-info" />
                                    </p>
                                </div>
                                <div className="field-group">
                                    <label className="login-field" htmlFor="recoveryVerificationCode">
                                        <AppText textKey="verify-mfa-recovery-code-label" />
                                    </label>
                                    <Input
                                        autoComplete="recovery-code"
                                        className="autofocus"
                                        disabled={isLoading}
                                        onChange={onSetVerificationCode}
                                        id="recoveryVerificationCode"
                                        value={verificationCode}
                                        placeholder="12345-67890"
                                        fullWidth={true}
                                        autoFocus={true}
                                        htmlAttributes={{
                                            maxLength: 11,
                                            pattern: "^[0-9A-Fa-F]{5}-[0-9A-Fa-F]{5}$",
                                            spellCheck: false,
                                            autoCorrect: "off",
                                            autoCapitalize: "none",
                                        }}
                                    />
                                </div>
                                {allowRememberMe && (
                                    <Checkbox
                                        className="login-field"
                                        identifier="rememberMe"
                                        checked={rememberMe}
                                        onChange={handleChangeRememberMe}
                                        label={
                                            rememberMeDays
                                                ? interpolateLocaleString(appStrings["verify-mfa-remember-me-in-days-label"], rememberMeDays ? { days: rememberMeDays } : undefined)
                                                : appStrings["verify-mfa-remember-me-label"]
                                        }
                                    />
                                )}
                                <Button intent="primary" disabled={isLoading || !isValidRecoveryCode(verificationCode)} type="submit">
                                    <AppText textKey="verify-mfa-submit-label" />
                                </Button>
                            </div>

                            {isTotpEnabled && (
                                <div className="mfa-section">
                                    <Button disabled={isLoading} onClick={toggleUseRecoveryCode} type="button">
                                        <AppText textKey="verify-mfa-use-totp-code-label" />
                                    </Button>
                                </div>
                            )}

                        </>}
                        {(isTotpEnabled || hasRecoveryCodes) && (
                            <div className="field-group">
                                <div id="cf-turnstile" className="cf-turnstile"></div>
                            </div>
                        )}
                    </>}
                </form>
                {!showLoadingInit &&
                  <Links forceIsLogin={true} />
                }
            </div>
        </main>
    );
};

export default VerifyMfa;
